import { useGetCareOrdersQuery } from '@src/providers/AppStateProvider/streamingDataSlice';
import { SizeModeEnum, useCallbackRef, type CareOrder } from '@talos/kyoko';
import { useCallback, useMemo, useState } from 'react';
import { SelectableCareOrder, type SelectableCareOrderSelectionState } from './SelectableCareOrder';
import {
  isExistingCareOrderNettingPreviewMode,
  type CareOrderNettingPreviewMode as CareOrderNettingPreviewModeType,
} from './useWorkingOrderPreview';

export interface CareOrderSelectionChange {
  orderID: string;
  selected: boolean;
}

/**
 * Seeds the dialog with the care-order IDs that should drive preview requests before streamed rows render.
 */
function getInitialSelectionStateByID(
  initialMemberCareOrders: CareOrder[],
  mode: CareOrderNettingPreviewModeType,
  nettedCareOrder?: CareOrder
) {
  const isExistingNettingMode = isExistingCareOrderNettingPreviewMode(mode);
  const memberCareOrderIDsForMode = isExistingNettingMode
    ? (nettedCareOrder?.MemberCareOrders?.map(memberCareOrder => memberCareOrder.OrderID) ??
      initialMemberCareOrders.map(careOrder => careOrder.OrderID))
    : initialMemberCareOrders.map(careOrder => careOrder.OrderID);
  const nettedMemberCareOrderIDs = new Set(
    nettedCareOrder?.MemberCareOrders?.map(memberCareOrder => memberCareOrder.OrderID) ?? []
  );

  return new Map<string, SelectableCareOrderSelectionState>(
    memberCareOrderIDsForMode.map(orderID => [
      orderID,
      {
        selected: true,
        hasBeenSelected: true,
        inNettedOrder: isExistingNettingMode ? nettedMemberCareOrderIDs.has(orderID) : false,
      },
    ])
  );
}

/**
 * Applies row selection changes to a cloned map so batched UI events can compose through functional state updates.
 */
function getNextSelectionStateByID(
  selectionStateByID: Map<string, SelectableCareOrderSelectionState>,
  selectionChanges: CareOrderSelectionChange[]
) {
  const nextSelection = new Map(selectionStateByID);

  selectionChanges.forEach(({ orderID, selected }) => {
    const previousSelectionState = nextSelection.get(orderID);
    nextSelection.set(orderID, {
      selected,
      hasBeenSelected: true,
      inNettedOrder: previousSelectionState?.inNettedOrder ?? false,
    });
  });

  return nextSelection;
}

/**
 * Wraps streamed care-order rows with local selection metadata for the member and compatible blotters.
 */
function getSelectableCareOrders(
  selectionStateByID: Map<string, SelectableCareOrderSelectionState>,
  careOrders: CareOrder[]
) {
  return careOrders.map(careOrder => new SelectableCareOrder(careOrder, selectionStateByID.get(careOrder.OrderID)));
}

/**
 * Reads selected IDs from state so preview requests are not blocked by websocket row availability.
 */
function getSelectedCareOrderIDs(selectionStateByID: Map<string, SelectableCareOrderSelectionState>) {
  return [...selectionStateByID.entries()]
    .filter(([, selectionState]) => selectionState.selected)
    .map(([orderID]) => orderID);
}

/**
 * Guards the Units size mode against selections containing amount-based care orders.
 */
function canSelectUnits(memberCareOrders: CareOrder[]) {
  return memberCareOrders.length > 0 && !memberCareOrders.some(careOrder => !careOrder.isQtyInUnits);
}

/**
 * Guards the Currency size mode against multi-token selections that can only be netted in units.
 */
function canSelectCurrency(memberCareOrders: CareOrder[]) {
  return memberCareOrders.length > 0 && !memberCareOrders.some(careOrder => careOrder.isMultitoken);
}

/**
 * Picks the starting size mode from shipped modify state or from the create-mode member capabilities.
 */
function getInitialNettingSizeMode({
  mode,
  initialMemberCareOrders,
  nettedCareOrder,
}: {
  mode: CareOrderNettingPreviewModeType;
  initialMemberCareOrders: CareOrder[];
  nettedCareOrder?: CareOrder;
}) {
  if (isExistingCareOrderNettingPreviewMode(mode)) {
    return nettedCareOrder?.SizeMode ?? SizeModeEnum.Currency; // No SizeMode on a WO = Currency
  }

  return canSelectCurrency(initialMemberCareOrders) ? SizeModeEnum.Currency : SizeModeEnum.Units;
}

/**
 * Encapsulates selectable care-order state for netting preview.
 *
 * Returns:
 * - `allCareOrders`: streamed care orders enriched with local selection state
 * - `memberCareOrders`: selected care orders with local selection metadata
 * - `memberCareOrderIDs`: currently selected member care-order IDs
 * - `allMemberCareOrdersLoaded`: whether every selected ID has matching streamed row data
 * - `careOrderSelectionStateByID`: selection state map by OrderID
 * - `onSelectionChanged`: handler for toggling care-order selection
 */
export function useSelectableCareOrders({
  mode,
  initialMemberCareOrders,
  nettedCareOrder,
}: {
  mode: CareOrderNettingPreviewModeType;
  initialMemberCareOrders: CareOrder[];
  nettedCareOrder?: CareOrder;
}) {
  const [careOrderSelectionStateByID, setCareOrderSelectionStateByID] = useState<
    Map<string, SelectableCareOrderSelectionState>
  >(() => getInitialSelectionStateByID(initialMemberCareOrders, mode, nettedCareOrder));
  const [sizeMode, setSizeMode] = useState(() =>
    getInitialNettingSizeMode({
      mode,
      initialMemberCareOrders,
      nettedCareOrder,
    })
  );
  const isExistingNettingMode = isExistingCareOrderNettingPreviewMode(mode);

  const { data: careOrdersByID } = useGetCareOrdersQuery({});
  const streamedCareOrders = useMemo(
    () => (careOrdersByID == null ? [] : [...careOrdersByID.values()]),
    [careOrdersByID]
  );

  const onSelectionChanged = useCallback(
    (selectionChanges: CareOrderSelectionChange[]) => {
      setCareOrderSelectionStateByID(previousSelectionStateByID => {
        const nextSelectionStateByID = getNextSelectionStateByID(previousSelectionStateByID, selectionChanges);

        // Selection changes come from streamed rows; use that row data to keep SizeMode valid.
        const nextMemberCareOrders = getSelectableCareOrders(nextSelectionStateByID, streamedCareOrders).filter(
          careOrder => careOrder.isSelected
        );

        if (!isExistingNettingMode) {
          if (!canSelectCurrency(nextMemberCareOrders)) {
            setSizeMode(SizeModeEnum.Units);
          } else if (!canSelectUnits(nextMemberCareOrders)) {
            setSizeMode(SizeModeEnum.Currency);
          }
        }

        return nextSelectionStateByID;
      });
    },
    [isExistingNettingMode, streamedCareOrders]
  );

  const allCareOrders = useMemo<SelectableCareOrder[]>(
    () => getSelectableCareOrders(careOrderSelectionStateByID, streamedCareOrders),
    [careOrderSelectionStateByID, streamedCareOrders]
  );

  const memberCareOrders = useMemo<SelectableCareOrder[]>(
    () => allCareOrders.filter(careOrder => careOrder.isSelected),
    [allCareOrders]
  );

  const memberCareOrderIDs = useMemo(
    () => getSelectedCareOrderIDs(careOrderSelectionStateByID),
    [careOrderSelectionStateByID]
  );

  // Selected IDs can exist before their rows: initial dialog state seeds selection by ID,
  // while `memberCareOrders` is derived from websocket rows. Preview requests need rows
  // for derived fields such as inherited SubAccount.
  const allMemberCareOrdersLoaded = memberCareOrders.length === memberCareOrderIDs.length;

  const canSelectUnitsSizeMode = useMemo(() => canSelectUnits(memberCareOrders), [memberCareOrders]);
  const canSelectCurrencySizeMode = useMemo(() => canSelectCurrency(memberCareOrders), [memberCareOrders]);

  const isSizeModeDisabled = isExistingNettingMode || !canSelectCurrencySizeMode || !canSelectUnitsSizeMode;
  const onSizeModeChange = useCallbackRef((nextSizeMode: SizeModeEnum) => {
    // Existing netting modes must keep SizeMode aligned with the persisted WorkingOrder.
    if (
      isExistingNettingMode ||
      (nextSizeMode === SizeModeEnum.Currency && !canSelectCurrencySizeMode) ||
      (nextSizeMode === SizeModeEnum.Units && !canSelectUnitsSizeMode)
    ) {
      return;
    }

    setSizeMode(nextSizeMode);
  });

  return {
    allCareOrders,
    careOrdersByID,
    memberCareOrders,
    memberCareOrderIDs,
    allMemberCareOrdersLoaded,
    careOrderSelectionStateByID,
    sizeMode,
    canSelectUnitsSizeMode,
    canSelectCurrencySizeMode,
    isSizeModeDisabled,
    onSizeModeChange,
    onSelectionChanged,
  };
}
